import React, { useState, useEffect } from 'react';
import {
  Grid, Box, TextField, Select, MenuItem, FormControl, InputLabel,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, CircularProgress, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
  getallemployeesByAirportForEdit,
  getallemployeesWithZones,
} from '../logic';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ACTIONS } from '../../../../shared/constants';
import { useSnackbar } from 'notistack';
import { getUser } from '../../../../shared/auth-service';

const ManagementZoneDetail = ({
  data,
  handleClose,
  handleSave,
  employeeId,
  zones,
  airports,
  airportId,
  zonesSelected,
  action
}) => {
  const [selectedEmployeeId] = useState(employeeId);
  const [selectedCodes, setSelectedCodes] = useState(data || []);
  const [selectedAirport, setSelectedAirport] = useState(airportId);
  const isDataReadOnly = action !== ACTIONS.NEW;
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [employeeSelected, setEmployeeSelected] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedZoneValue, setSelectedZoneValue] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(action !== ACTIONS.NEW);
  const userData = getUser();
  const companyId = userData.countryCode.split('-')[0];

  useEffect(() => {
    const fetchData = async () => {
      setLoadingEmployees(true);
      const employeesData = isDataReadOnly
        ? await getallemployeesByAirportForEdit(selectedAirport, companyId)
        : await getallemployeesWithZones(selectedAirport, companyId);
      setEmployees(employeesData);
      setLoadingEmployees(false);

      if (isDataReadOnly) {
        const found = employeesData.find(e => e.employeeId === selectedEmployeeId);
        setEmployeeSelected(found ?? null);
      } else {
        setSelectedCodes([]);
      }
    };
    fetchData();
  }, [action, employeeId, selectedAirport]);

  const handleAddZone = (zoneObj) => {
    if (!zoneObj || selectedCodes.some(el => el.id === zoneObj.id)) return;
    const updated = [...selectedCodes, { id: zoneObj.id, description: zoneObj.description }];
    setSelectedCodes(updated);
    setIsDirty(true);
    setAccordionOpen(true);
  };

  const onSave = () => {
    const resolvedEmployeeId = employeeSelected?.employeeId || (isDataReadOnly ? selectedEmployeeId : null);
    if (!resolvedEmployeeId || selectedCodes.length === 0) {
      enqueueSnackbar('Debe ingresar todos los datos', { variant: 'error' });
      return;
    }
    handleSave({
      zones: selectedCodes.map(({ description, id }) => ({ Description: description, Id: id })),
      employeeId: resolvedEmployeeId,
      action: isDataReadOnly ? 'MODIFICAR' : 'CREAR'
    });
  };

  return (
    <>
      <Dialog maxWidth="md" fullWidth open>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold" style={{ color: '#1a3072' }}>
            Zonas Adicionales Por <span style={{ color: '#00b0ef' }}>Empleado</span>
          </Typography>
        </DialogTitle>

        <DialogContent>
          {/* Datos del empleado */}
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e4ed' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
              Datos del Empleado
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Base</InputLabel>
                  <Select
                    label="Base"
                    value={selectedAirport}
                    onChange={(e) => { setSelectedAirport(e.target.value); setEmployeeSelected(null); }}
                    disabled={isDataReadOnly}
                  >
                    <MenuItem value={0}>Todas</MenuItem>
                    {airports.map(item => (
                      <MenuItem key={item.id} value={item.id}>{item.code}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={9}>
                {loadingEmployees ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Cargando empleados...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    noOptionsText="Sin resultados"
                    size="small"
                    options={employees}
                    getOptionLabel={(o) => o ? `${o.employeeId} - ${o.name} ${o.lastName}` : ''}
                    value={employeeSelected}
                    onChange={(_, newValue) => { setEmployeeSelected(newValue); setIsDirty(true); }}
                    disabled={isDataReadOnly}
                    renderInput={(params) => <TextField {...params} label="Empleado" />}
                  />
                )}
              </Grid>
              {employeeSelected?.employeePosition?.name && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Cargo:</Typography>
                    <Typography variant="body2" sx={{ color: '#1a3072', fontWeight: 500 }}>
                      {employeeSelected.employeePosition.name}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Asignación de zonas */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
            Asignación de Zonas
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              options={zonesSelected}
              getOptionLabel={(o) => o.description || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedZoneValue}
              onChange={(_, newValue) => {
                setSelectedZoneValue(newValue);
                if (newValue) handleAddZone(newValue);
              }}
              renderInput={(params) => <TextField {...params} label="Seleccionar zona" variant="outlined" size="small" />}
              noOptionsText="Sin resultados"
            />
          </FormControl>

          {selectedCodes.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center', color: '#aaa' }}>
              <Typography variant="body2">Sin zonas asignadas</Typography>
            </Box>
          ) : (
            <Accordion expanded={accordionOpen} disableGutters elevation={0}
              onChange={(_, isExpanded) => setAccordionOpen(isExpanded)}
              sx={{ border: '1px solid #e0e4ed', borderRadius: '8px !important', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: '#f0f4ff', borderRadius: '8px', minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      Zonas
                    </Typography>
                    <Chip label={selectedCodes.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#1a3072', color: '#fff' }} />
                  </Box>
                  <Chip
                    icon={<DeleteSweepIcon sx={{ fontSize: '14px !important' }} />}
                    label="Quitar todo"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setSelectedCodes([]); setIsDirty(true); setAccordionOpen(false); }}
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#e53935', border: '1px solid #fecaca', '& .MuiChip-icon': { color: '#e53935' }, '&:hover': { bgcolor: '#fee2e2' } }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedCodes.map(z => (
                    <Chip
                      key={z.id}
                      label={z.description}
                      size="small"
                      onDelete={() => { setSelectedCodes(prev => prev.filter(el => el.id !== z.id)); setIsDirty(true); }}
                      sx={{ bgcolor: '#e8f0fe', color: '#1a3072', fontWeight: 500, '& .MuiChip-deleteIcon': { color: '#e53935' } }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </DialogContent>

        <DialogActions sx={{ m: 1 }}>
          <Button color="primary" variant="contained" onClick={onSave} endIcon={<SaveIcon />}>
            {ACTIONS.SAVE}
          </Button>
          <Box sx={{ m: 1 }}>
            <Button endIcon={<CancelIcon />} variant="outlined"
              onClick={() => isDirty ? setShowCancelAlert(true) : handleClose()}>
              {ACTIONS.CANCEL}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={showCancelAlert} maxWidth="xs" fullWidth onClose={() => setShowCancelAlert(false)}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 56, color: '#f59e0b', mb: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a3072', mb: 1 }}>¿Descartar cambios?</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Tiene cambios sin guardar. Si sale ahora, se perderán todos los cambios realizados.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button variant="outlined" onClick={() => setShowCancelAlert(false)}
            sx={{ minWidth: 140, borderColor: '#1a3072', color: '#1a3072', '&:hover': { borderColor: '#1a3072', bgcolor: '#f0f4ff' } }}>
            Seguir editando
          </Button>
          <Button variant="contained" onClick={() => { setShowCancelAlert(false); handleClose(); }}
            sx={{ minWidth: 140, bgcolor: '#e53935', '&:hover': { bgcolor: '#b71c1c' } }}>
            Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagementZoneDetail;
